import * as stylex from '@stylexjs/stylex';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  notFound,
  redirect,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { useState } from 'react';
import {
  DialogTrigger,
  Input,
  ResizableTableContainer,
  SearchField,
  Table,
  TableLayout,
  Virtualizer,
} from 'react-aria-components';
import { fromEntries as rFromEntries, map as rMap, pick as rPick, pipe as rPipe } from 'remeda';
import {
  array as vArray,
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
  unknown as vUnknown,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { TableRowSecondaryActions } from '@/components/table-row-secondary-actions.component';
import {
  AddPlusStrokeIcon,
  Breadcrumb,
  Breadcrumbs,
  Cell,
  CloseStrokeIcon,
  Column,
  EmptyState,
  GridList,
  GridListItem,
  Heading,
  IconButton,
  MenuItem,
  Popover,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  Row,
  Skeleton,
  type SkeletonRow,
  TableBody,
  TableHeader,
  TableOverlay,
  TAG_PICKER_SLOTS,
  TagPicker,
  Text,
  TOAST_TIMEOUT,
  useLocalStorage,
  useMediaQuery,
  View,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { useDebounce } from '@/libs/serenity-ui/hooks/use-debounce/use-debounce.hook';
import { RouterLink } from '@/libs/tanstack-router';
import { campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { toApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import { campaignFilterListAdapter } from '@/resources/campaign-request/campaign-request.filter-list-adapter';
import { campaignRequestTableInitialConfig, campaignRequestTableOptions } from '@/resources/campaign-request/campaign-request.table';
import { campaignRequestProgramMutations, campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { campaignRequestProgramPolicies } from '@/resources/campaign-request-program/campaign-request-program.policies';
import { organizationTeamQueries } from '@/resources/organization-team/organization-team.api';
import { organizationUserQueries } from '@/resources/organization-user/organization-user.api';
import { ActiveFilters } from '@/routes/_main/_list/-components/active-filters.component';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Search } from '@/routes/_main/_list/-components/search.component';
import { CampaignRequestsFiltersForm } from '@/routes/_main/_list/campaigns/-components/campaign-requests-filters-form.component';
import { type QueryResultData } from '@/types/query-result-data.type';
import { Resource } from '@/types/resource.type';

type CampaignRequestRow = QueryResultData<typeof campaignRequestQueries.list>['data'][number];

const DEFAULT_SORTING = { id: 'updatedAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 50 };
const SEARCH_FILTER_ID = 'campaignName';

export const Route = createFileRoute('/_main/_list/programs/$programId/campaigns')({
  component: RouteComponent,
  params: {
    parse: ({ programId }) => ({ programId: vParse(vPipe(vString(), vTransform(Number), vNumber()), programId) }),
    stringify: ({ programId }) => ({ programId: String(programId) }),
  },
  validateSearch: vObject({
    pagination: vFallback(vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING), DEFAULT_PAGING),
    columnFilters: vFallback(vNullish(vArray(vObject({ id: vString(), value: vUnknown() })), []), []),
    sorting: vFallback(vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING), DEFAULT_SORTING),
  }),
  beforeLoad: ({ search, params, context }) => {
    if (context.user && !context.programsFeatureEnabled) {
      throw redirect({ to: '/campaigns' });
    }

    if (typeof (params.programId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequestPrograms.view()) {
      throw redirect({ to: '/' });
    }

    const ownerFilter = search.columnFilters.find((f) => f.id === 'ownerId');
    const teamFilter = search.columnFilters.find((f) => f.id === 'teamId');

    return {
      resource: `${Resource.campaignRequestProgram}-campaigns` as Resource,
      can: {
        [Resource.campaignRequestProgram as const]: rPick(
          campaignRequestProgramPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }),
          [ 'update' ],
        ),
      },
      queryOptions: {
        list: campaignRequestQueries.list({
          filter: {
            programId: params.programId,
            ...toApiCampaignRequestFieldAdapter.transform(rPipe(
              search.columnFilters,
              rMap(({ id, value }) => [ id, value ] as const),
              rFromEntries(),
            )),
          },
          sorting: search.sorting,
          paging: search.pagination,
        }),
        programDetail: campaignRequestProgramQueries.detail(params.programId),
        organizationUserListFilter: organizationUserQueries.list(ownerFilter ? { id: ownerFilter.value as string } : undefined),
        organizationTeamListFilter: organizationTeamQueries.list(teamFilter ? { id: teamFilter.value as number } : undefined),
      },
      hasOwnerFilter: !!ownerFilter,
      hasTeamFilter: !!teamFilter,
    };
  },
  loader: ({ context }) => {
    if (!context.user) {
      return;
    }

    context.queryClient.prefetchQuery(context.queryOptions.programDetail);
    context.queryClient.prefetchQuery(context.queryOptions.list);
    context.hasOwnerFilter && context.queryClient.prefetchQuery(context.queryOptions.organizationUserListFilter);
    context.hasTeamFilter && context.queryClient.prefetchQuery(context.queryOptions.organizationTeamListFilter);
  },
  head: () => ({
    meta: [{ title: 'TrendMD | Program - Campaigns' }],
  }),
  staticData: {
    list: {
      heading: 'Campaigns',
      slots: {
        topBarComponent: TopBar,
        toolbarActionsComponent: ToolbarActionsComponent,
        toolbarLeftComponent: ToolbarLeftComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const routeParams = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/_main/_list/programs/$programId/campaigns' });

  const [ columnsConfig ] = useLocalStorage(`table-columns-${routeContext.resource}`, campaignRequestTableInitialConfig.filter((c) => c.id !== 'program'));

  const programDetailQuery = useQuery(routeContext.queryOptions.programDetail);
  const query = useQuery(routeContext.queryOptions.list);

  const unlinkMutation = useMutation(campaignRequestProgramMutations.unlinkCampaignRequest);

  const handleUnlink = (campaignRequestId: number): void => {
    unlinkMutation.mutate(
      { id: routeParams.programId, campaignRequestId },
      {
        onSuccess: () => {
          routeContext.queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
          routeContext.queryClient.invalidateQueries({ queryKey: campaignRequestQueries.all.queryKey });
        },
        onError: (error) => {
          routeContext.toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
        },
      },
    );
  };

  const canUpdate = programDetailQuery.data != null && routeContext.can.campaignRequestProgram.update(programDetailQuery.data.original);

  const dataRows: CampaignRequestRow[] = query.isLoading ? [] : (query.data?.data ?? []);

  const config = campaignRequestTableOptions(dataRows, columnsConfig);
  const columns = config.getColumns();

  const [ hasMeasuredData, setHasMeasuredData ] = useState(false);
  if (!hasMeasuredData && dataRows.length > 0) {
    setHasMeasuredData(true);
  }

  const rows: Array<CampaignRequestRow | SkeletonRow> = query.isLoading ? config.getSkeletonRows() : dataRows;

  return (
    <TableOverlay
      isFullScreen
      rows={rows}
      isRowHighlighted={(row) => {
        if (config.isSkeletonRow(row)) {
          return false;
        }
        return location.hash === `campaign-${row.original.id}`;
      }}
      renderEmptyState={() => (
        !query.isLoading && <EmptyState><Heading>No campaigns linked</Heading></EmptyState>
      )}
      renderRowActions={(row) => {
        if (config.isSkeletonRow(row)) {
          return null;
        }
        if (!canUpdate) {
          return (
            <TableRowSecondaryActions
              hash={`campaign-${row.original.id}`}
              id={row.original.id.toString()}
            />
          );
        }
        return (
          <TableRowSecondaryActions
            hash={`campaign-${row.original.id}`}
            id={row.original.id.toString()}>
            {({ defaultChildren }) => (
              <>
                {defaultChildren}
                <MenuItem onAction={() => handleUnlink(row.original.id)}>
                  <CloseStrokeIcon />
                  <Text>Unlink</Text>
                </MenuItem>
              </>
            )}
          </TableRowSecondaryActions>
        );
      }}>
      <Virtualizer layout={TableLayout} layoutOptions={config.layoutOptions}>
        <ResizableTableContainer key={hasMeasuredData ? 'data' : 'loading'} {...stylex.props(resizableTableContainerStyles.base)}>
          <Table
            aria-label="Campaigns"
            selectionMode="none"
            sortDescriptor={{ column: search.sorting.id, direction: search.sorting.desc ? 'descending' : 'ascending' }}
            style={{ borderSpacing: 0 }}
            onSortChange={(sortDescriptor) => {
              const newId = sortDescriptor.column.toString();
              navigate({
                to: location.pathname,
                search: (prev) => ({
                  ...prev,
                  sorting: {
                    id: newId,
                    desc: newId === prev.sorting?.id ? !prev.sorting.desc : false,
                  },
                }),
              });
            }}>
            <TableHeader columns={columns.filter((c) => c.id !== 'program')}>
              {(column) => <Column {...column} isFullScreen />}
            </TableHeader>
            <TableBody<typeof rows[number]> dependencies={[ columns, query.isLoading ]}>
              {(item) => {
                if (config.isSkeletonRow(item)) {
                  return (
                    <Row columns={columns.filter((c) => c.id !== 'program')} id={item.id}>
                      {() => <Cell isFullScreen><Skeleton /></Cell>}
                    </Row>
                  );
                }

                const row = item as CampaignRequestRow;
                return (
                  <Row columns={columns.filter((c) => c.id !== 'program')} id={row.original.id} variant="primary">
                    {(column) => {
                      const cellProps = config.getCell(column.id, row);
                      return cellProps ? <Cell {...cellProps} isFullScreen /> : <Cell isFullScreen />;
                    }}
                  </Row>
                );
              }}
            </TableBody>
          </Table>
        </ResizableTableContainer>
      </Virtualizer>
    </TableOverlay>
  );
}

function TopBar(): ReactElement {
  const routeParams = Route.useParams();
  const detailQuery = useQuery(campaignRequestProgramQueries.detail(routeParams.programId));

  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/programs">Programs</RouterLink>
      </Breadcrumb>
      <Breadcrumb>
        <RouterLink params={routeParams} style={breadcrumbStyles.dynamic} to="/programs/$programId">
          {detailQuery.data?.original.name}
        </RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink params={routeParams} to="/programs/$programId/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function ToolbarActionsComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const routeParams = Route.useParams();
  const isSmallScreen = useMediaQuery(breakpoints.small);

  const [ isLinkOpen, setIsLinkOpen ] = useState(false);
  const [ linkSearch, setLinkSearch ] = useState('');
  const debouncedLinkSearch = useDebounce(linkSearch, PROGRESS_INDICATOR_MIN_DELAY_DURATION);

  const programDetailQuery = useQuery(routeContext.queryOptions.programDetail);
  const canUpdate = programDetailQuery.data != null && routeContext.can.campaignRequestProgram.update(programDetailQuery.data.original);

  const linkSearchQuery = useQuery(
    campaignRequestQueries.list({
      paging: { pageIndex: 0, pageSize: 20 },
      filter: { campaignName: debouncedLinkSearch || undefined },
    }),
  );

  const linkMutation = useMutation(campaignRequestProgramMutations.linkCampaignRequest);

  const handleLink = (campaignRequestId: number): void => {
    linkMutation.mutate(
      { id: routeParams.programId, campaignRequestId },
      {
        onSuccess: () => {
          routeContext.queryClient.invalidateQueries({ queryKey: campaignRequestProgramQueries.all.queryKey });
          routeContext.queryClient.invalidateQueries({ queryKey: campaignRequestQueries.all.queryKey });
          setIsLinkOpen(false);
        },
        onError: (error) => {
          routeContext.toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
        },
      },
    );
  };

  const linkSearchItems = (linkSearchQuery.data?.data ?? []).filter(
    (item) => item.original.program == null,
  );

  const linkPopoverContent = (
    <TagPicker>
      <View slot={TAG_PICKER_SLOTS.INPUT_VIEW}>
        <SearchField
          aria-label="Search campaigns"
          value={linkSearch}
          onChange={setLinkSearch}>
          <Input placeholder="Search" />
        </SearchField>
      </View>
      <GridList
        aria-label="Campaigns"
        items={linkSearchItems}
        renderEmptyState={() => (!linkSearchQuery.isPending ? <Text color="quaternary">No campaigns found</Text> : null)}>
        {(item) => (
          <GridListItem
            id={item.original.id}
            key={item.original.id}
            textValue={item.original.campaignName}
            onAction={() => handleLink(item.original.id)}>
            <Text>{item.original.campaignName}</Text>
          </GridListItem>
        )}
      </GridList>
    </TagPicker>
  );

  return (
    <>
      {canUpdate && (
        <DialogTrigger isOpen={isLinkOpen} onOpenChange={setIsLinkOpen}>
          <IconButton aria-label="Link campaign request">
            <AddPlusStrokeIcon />
          </IconButton>
          <Popover placement="bottom end">{linkPopoverContent}</Popover>
        </DialogTrigger>
      )}
      {!isSmallScreen && <SecondaryActionsIconButton isTableActions />}
    </>
  );
}

function ToolbarLeftComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const isSmallScreen = useMediaQuery(breakpoints.small);

  const organizationUserListFilterQuery = useQuery({
    ...routeContext.queryOptions.organizationUserListFilter,
    enabled: routeContext.hasOwnerFilter as boolean,
  });
  const organizationTeamListFilterQuery = useQuery({
    ...routeContext.queryOptions.organizationTeamListFilter,
    enabled: routeContext.hasTeamFilter as boolean,
  });

  return (
    <>
      <Search resource={routeContext.resource as Resource} searchFilterId={SEARCH_FILTER_ID}>
        {(renderProps) => (
          <CampaignRequestsFiltersForm
            from="/_main/_list/programs/$programId/campaigns"
            onClose={renderProps.close}
          />
        )}
      </Search>
      <ActiveFilters
        searchFilterId={SEARCH_FILTER_ID}
        filterListAdapter={(filters) =>
          campaignFilterListAdapter(filters, {
            owner: organizationUserListFilterQuery.data?.data?.[0]?.original,
            team: organizationTeamListFilterQuery.data?.data?.[0]?.original,
            isOwnerPending: organizationUserListFilterQuery.isPending,
            isTeamPending: organizationTeamListFilterQuery.isPending,
          })
        }
      />
      {isSmallScreen && <SecondaryActionsIconButton isTableActions />}
    </>
  );
}

const resizableTableContainerStyles = stylex.create({
  base: {
    height: '100%',
    overflow: 'auto',
    scrollbarWidth: 'none',
    width: '100%',
    willChange: 'transform',
  },
});

const breadcrumbStyles = stylex.create({
  dynamic: {
    display: 'block',
    maxWidth: 200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
